<div class="account-creation-wrapper">
  <!-- Page Header -->
  <div class="creation-page-header">
    <h1 class="page-main-title">Create User Account</h1>
    <p class="page-subtitle">Add a new user to the AUCDT Management System</p>
  </div>

  <!-- Success Alert -->
  <div class="alert-notification alert-success" *ngIf="submitSuccess">
    <div class="alert-icon">✓</div>
    <div class="alert-content">
      <strong>Success!</strong> {{ successMessage }}
    </div>
    <button class="alert-close" (click)="submitSuccess = false">×</button>
  </div>

  <!-- Error Alert -->
  <div class="alert-notification alert-error" *ngIf="submitError">
    <div class="alert-icon">✕</div>
    <div class="alert-content">
      <strong>Error!</strong> {{ errorMessage }}
    </div>
    <button class="alert-close" (click)="submitError = false">×</button>
  </div>

  <!-- User Account Form -->
  <div class="account-form-panel">
    <form [formGroup]="userAccountForm" (ngSubmit)="onSubmit()">
      
      <!-- Personal Information Section -->
      <div class="form-section">
        <h2 class="section-heading">Personal Information</h2>
        
        <div class="form-row">
          <!-- First Name -->
          <div class="form-field">
            <label class="field-label" for="firstName">
              First Name <span class="required-mark">*</span>
            </label>
            <input 
              type="text" 
              id="firstName"
              class="field-input"
              formControlName="firstName"
              placeholder="Enter first name"
              [class.input-error]="hasError('firstName', 'required') || hasError('firstName', 'minlength')"
            />
            <div class="field-error" *ngIf="hasError('firstName', 'required')">
              First name is required
            </div>
            <div class="field-error" *ngIf="hasError('firstName', 'minlength')">
              First name must be at least 2 characters
            </div>
          </div>

          <!-- Last Name -->
          <div class="form-field">
            <label class="field-label" for="lastName">
              Last Name <span class="required-mark">*</span>
            </label>
            <input 
              type="text" 
              id="lastName"
              class="field-input"
              formControlName="lastName"
              placeholder="Enter last name"
              [class.input-error]="hasError('lastName', 'required') || hasError('lastName', 'minlength')"
            />
            <div class="field-error" *ngIf="hasError('lastName', 'required')">
              Last name is required
            </div>
            <div class="field-error" *ngIf="hasError('lastName', 'minlength')">
              Last name must be at least 2 characters
            </div>
          </div>
        </div>

        <div class="form-row">
          <!-- Other Names -->
          <div class="form-field">
            <label class="field-label" for="otherNames">
              Other Names <span class="optional-mark">(Optional)</span>
            </label>
            <input 
              type="text" 
              id="otherNames"
              class="field-input"
              formControlName="otherNames"
              placeholder="Enter other names"
            />
          </div>
        </div>
      </div>

      <!-- Account Information Section -->
      <div class="form-section">
        <h2 class="section-heading">Account Information</h2>
        
        <div class="form-row">
          <!-- Username -->
          <div class="form-field">
            <label class="field-label" for="username">
              Username <span class="required-mark">*</span>
            </label>
            <input 
              type="text" 
              id="username"
              class="field-input"
              formControlName="username"
              placeholder="Enter username"
              [class.input-error]="hasError('username', 'required') || hasError('username', 'minlength')"
            />
            <div class="field-error" *ngIf="hasError('username', 'required')">
              Username is required
            </div>
            <div class="field-error" *ngIf="hasError('username', 'minlength')">
              Username must be at least 3 characters
            </div>
          </div>

          <!-- Email -->
          <div class="form-field">
            <label class="field-label" for="email">
              Email <span class="required-mark">*</span>
            </label>
            <input 
              type="email" 
              id="email"
              class="field-input"
              formControlName="email"
              placeholder="firstname.lastname@techbridge.edu.gh"
              [class.input-error]="hasError('email', 'required') || hasError('email', 'email') || hasError('email', 'pattern')"
            />
            <div class="field-error" *ngIf="hasError('email', 'required')">
              Email is required
            </div>
            <div class="field-error" *ngIf="hasError('email', 'email')">
              Please enter a valid email address
            </div>
            <div class="field-error" *ngIf="hasError('email', 'pattern')">
              Email must be an @techbridge.edu.gh address
            </div>
          </div>
        </div>
      </div>

      <!-- Role Assignment Section -->
      <div class="form-section">
        <h2 class="section-heading">Role Assignment</h2>
        
        <div class="form-row">
          <!-- Admin Type -->
          <div class="form-field">
            <label class="field-label" for="adminType">
              User Role <span class="required-mark">*</span>
            </label>
            <select 
              id="adminType"
              class="field-select"
              formControlName="adminType"
              [class.input-error]="hasError('adminType', 'required')"
            >
              <option value="">Select a role</option>
              <option *ngFor="let type of adminTypes" [value]="type.value">
                {{ type.label }}
              </option>
            </select>
            <div class="field-error" *ngIf="hasError('adminType', 'required')">
              User role is required
            </div>
            <div class="field-hint">
              Select the appropriate role for this user based on their responsibilities
            </div>
          </div>
        </div>
      </div>

      <!-- Form Actions -->
      <div class="form-actions">
        <button 
          type="button" 
          class="btn-action btn-secondary"
          (click)="onReset()"
          [disabled]="isSubmitting"
        >
          Reset Form
        </button>
        <button 
          type="submit" 
          class="btn-action btn-primary"
          [disabled]="isSubmitting || userAccountForm.invalid"
        >
          <span *ngIf="!isSubmitting">Create Account</span>
          <span *ngIf="isSubmitting">Creating...</span>
        </button>
      </div>
    </form>
  </div>

  <!-- Information Panel -->
  <div class="info-panel">
    <div class="info-icon">
        <i class="fa fa-info"></i>
    </div>
    <div class="info-content">
      <h3 class="info-title">Important Notes</h3>
      <ul class="info-list">
        <li>All fields marked with <span class="required-mark">*</span> are mandatory</li>
        <li>Email must be a valid AUCDT institutional address (@aucdt.edu.gh)</li>
        <li>Username must be unique and at least 3 characters long</li>
        <li>A temporary password will be generated and sent to the user's email</li>
        <li>Users will be required to change their password on first login</li>
      </ul>
    </div>
  </div>
</div>